// api/user.ts
import {getApi} from './apiCommon';
import {UserInfo} from '../framework2-sdk/f2Api';
import config from '../config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface User {
    name: string;
    surname: string;
    email: string;
}

export interface CompanyRef {
    __typename?: string;
    uid: string;
    Name: string;
    profileImage?: string;
}

export interface CompanyHistoryEntry {
    startedAt: string;
    endedAt: string | null;
    company: CompanyRef | null;
}

export interface InvitationEntry {
    ID: string;
    createdAt: string;
    usedAt: string | null;
    name: string;
    surname: string;
    email: string;
    status: 'PENDING' | 'ACCEPTED' | 'REJECTED';
    admin: boolean;
    invitedCompany?: CompanyRef | null;
}

export interface NamedRef {
    uid: string;
    Name?: string;
    name?: string;
    profileImage?: string;
}

export interface CategoryRef {
    ID: string;
    Name: string;
}

export interface FullUser {
    F2_ID: number;
    name: string;
    surname: string;
    email: string;
    hasCompletedOnboarding: boolean;
    // Raw typed fields returned by the API (concrete types, no abstract resolution).
    currentCustomer?: CompanyRef | null;
    currentSupplier?: CompanyRef | null;
    currentCompanyConnection?: { edges: { admin: boolean; startDate?: string }[] };
    // Derived client-side from the two typed fields above (see deriveCurrentCompany).
    currentCompany?: (CompanyRef & { __typename: 'Customer' | 'Supplier' }) | null;
    companyHistory: CompanyHistoryEntry[];
    invitationsSent: InvitationEntry[];
    invitationsReceived: InvitationEntry[];
    // Additional read-only data
    favoriteSuppliers: NamedRef[];
    favoriteProducts: NamedRef[];
    likedFoods: CategoryRef[];
    dislikedFoods: CategoryRef[];
    postsAggregate?: { count: number };
    boughtAggregate?: { count: number };
    commentsAggregate?: { count: number };
}

export type UserFilterKey = 'all' | 'unconnected' | 'customers' | 'suppliers' | 'admins';

// ---------------------------------------------------------------------------
// GraphQL selection sets
// ---------------------------------------------------------------------------

// Light shape for the list view (no history / invitations) keeps the roster fast.
// We read the concrete typed fields (currentCustomer / currentSupplier) instead of
// the Company interface field, so no runtime __resolveType is required.
const listUserGQL = `{
    F2_ID
    name
    surname
    email
    currentCustomer { uid Name profileImage }
    currentSupplier { uid Name profileImage }
    currentCompanyConnection { edges { admin } }
}`;

// Full shape for the selected user detail. Interface-typed relations
// (companyHistory.company, invitation.invitedCompany) use inline fragments so each
// concrete type resolves per-fragment.
const fullUserGQL = `{
    F2_ID
    name
    surname
    email
    hasCompletedOnboarding
    currentCustomer { uid Name profileImage }
    currentSupplier { uid Name profileImage }
    currentCompanyConnection { edges { admin startDate } }
    companyHistory {
        startedAt
        endedAt
        company {
            ... on Customer { __typename uid Name profileImage }
            ... on Supplier { __typename uid Name profileImage }
        }
    }
    invitationsSent {
        ID createdAt usedAt name surname email status admin
        invitedCompany {
            ... on Customer { __typename uid Name }
            ... on Supplier { __typename uid Name }
        }
    }
    invitationsReceived {
        ID createdAt usedAt name surname email status admin
        invitedCompany {
            ... on Customer { __typename uid Name }
            ... on Supplier { __typename uid Name }
        }
    }
    favoriteSuppliers { uid Name profileImage }
    favoriteProducts { uid Name }
    likedFoods { ID Name }
    dislikedFoods { ID Name }
    postsAggregate { count }
    boughtAggregate { count }
    commentsAggregate { count }
}`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Collapse the two typed fields into a single "current company" the UI can read.
export function deriveCurrentCompany(u: FullUser): FullUser {
    if (u.currentSupplier) {
        u.currentCompany = {...u.currentSupplier, __typename: 'Supplier'};
    } else if (u.currentCustomer) {
        u.currentCompany = {...u.currentCustomer, __typename: 'Customer'};
    } else {
        u.currentCompany = null;
    }
    return u;
}

// Single source of truth for the predefined filters.
// Built only from fields that exist on UserWhere in @neo4j/graphql 3.24:
// currentCustomer / currentCustomer_NOT, currentSupplier / currentSupplier_NOT,
// and the currentCompanyConnection edge filter. There is no `currentCompany`
// filter field, so it is never referenced here.
// Escape regex metacharacters so user input can't break the Neo4j MATCHES pattern.
function escapeRegex(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function buildUserFilter(key: UserFilterKey, search?: string): any {
    const clauses: any[] = [];

    if (search && search.length) {
        const rx = `(?i).*${escapeRegex(search)}.*`; // case-insensitive contains
        clauses.push({
            OR: [
                {name_MATCHES: rx},
                {surname_MATCHES: rx},
                {email_MATCHES: rx},
                {currentCustomerConnection: {node: {Name_MATCHES: rx}}},
                {currentSupplierConnection: {node: {Name_MATCHES: rx}}},
            ],
        });
    }

    switch (key) {
    case 'unconnected':
        clauses.push({currentCustomer: null});
        clauses.push({currentSupplier: null});
        break;
    case 'customers':
        clauses.push({currentCustomer_NOT: null});
        break;
    case 'suppliers':
        clauses.push({currentSupplier_NOT: null});
        break;
    case 'admins':
        clauses.push({currentCompanyConnection: {edges: {admin: true}}});
        break;
    case 'all':
    default:
        break;
    }

    if (clauses.length === 0) return undefined;
    if (clauses.length === 1) return clauses[0];
    return {AND: clauses};
}
// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export async function fetchUsers(
    filterKey: UserFilterKey = 'all',
    email?: string,
    options?: { offset?: number; limit?: number },
): Promise<FullUser[]> {
    const api = getApi();
    if (!api) return [];
    const where = buildUserFilter(filterKey, email);
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `query($where: UserWhere, $options: UserOptions) {
                users(where: $where, options: $options) ${listUserGQL}
            }`,
            variables: {where, options},
        },
    }).then((r) => (r.data?.users || []).map(deriveCurrentCompany));
}

export async function fetchFullUser(F2_ID: number): Promise<FullUser | null> {
    const api = getApi();
    if (!api) return null;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `query($where: UserWhere) { users(where: $where) ${fullUserGQL} }`,
            variables: {where: {F2_ID}},
        },
    }).then((r) => {
        const u = r.data?.users?.[0];
        return u ? deriveCurrentCompany(u) : null;
    });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

// Disconnect by matching the target node uid. PART_OF is shared across
// currentCompany / currentSupplier / currentCustomer, so we target the node,
// not the relationship field, to avoid hitting the wrong edge.
export async function disconnectUserFromCompany(F2_ID: number, companyUid: string): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `mutation($where: UserWhere, $disc: UserDisconnectInput) {
                updateUsers(where: $where, disconnect: $disc) { users { F2_ID } }
            }`,
            variables: {
                where: {F2_ID},
                disc: {currentCompany: {where: {node: {uid: companyUid}}}},
            },
        },
    }).then((r) => !!r.data?.updateUsers?.users?.length);
}

// Connect to a new company. The interface connect must be typed (Supplier | Customer).
export async function connectUserToCompany(
    F2_ID: number,
    companyUid: string,
    type: 'Supplier' | 'Customer',
    admin = false,
): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `mutation($where: UserWhere, $conn: UserConnectInput) {
                updateUsers(where: $where, connect: $conn) { users { F2_ID } }
            }`,
            variables: {
                where: {F2_ID},
                conn: {
                    currentCompany: {
                        [type]: {
                            where: {node: {uid: companyUid}},
                            edge: {admin, startDate: new Date().toISOString()},
                        },
                    },
                },
            },
        },
    }).then((r) => !!r.data?.updateUsers?.users?.length);
}

// History is written via the auto-generated createUserCompanyHistories mutation.
export async function recordCompanyHistory(
    F2_ID: number, companyUid: string, startedAt: string, endedAt: string,
): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `mutation($input: [UserCompanyHistoryCreateInput!]!) {
                createUserCompanyHistories(input: $input) { userCompanyHistories { startedAt } }
            }`,
            variables: {
                input: [{
                    startedAt, endedAt,
                    user: {connect: {where: {node: {F2_ID}}}},
                    company: {connect: {where: {node: {uid: companyUid}}}},
                }],
            },
        },
    }).then((r) => !!r.data?.createUserCompanyHistories?.userCompanyHistories?.length);
}

// Edit a user's scalar identity fields via the custom updateUser resolver.
// Authorization on the User type permits this for Admin-role JWTs.
export interface UpdateUserFields {
    name?: string;
    surname?: string;
    email?: string;
}

// Uses the auto-generated updateUsers (plural) — the same path the profile app
// uses successfully. The custom singular updateUser resolver returns null and is avoided.
export async function updateUserFields(F2_ID: number, update: UpdateUserFields): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `mutation($where: UserWhere!, $update: UserUpdateInput!) {
                updateUsers(where: $where, update: $update) {
                    users { F2_ID name surname email }
                }
            }`,
            variables: {
                where: {F2_ID},
                update: {
                    name: update.name,
                    surname: update.surname,
                    email: update.email,
                },
            },
        },
    }).then((r) => !!r.data?.updateUsers?.users?.length);
}

export async function deleteInvitation(ID: string): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {query: `mutation($ID: String!){ deleteInvitation(ID:$ID) }`, variables: {ID}},
    }).then((r) => !!r.data?.deleteInvitation);
}

export async function updateInvitationStatus(ID: string, expiresAt?: string): Promise<boolean> {
    const api = getApi();
    if (!api) return false;
    return api.callApi({
        url: config.graphqlURL,
        method: 'POST',
        data: {
            query: `mutation($ID: String!, $exp: DateTime){ updateInvitation(ID:$ID, expiresAt:$exp){ ID } }`,
            variables: {ID, exp: expiresAt},
        },
    }).then((r) => !!r.data?.updateInvitation);
}

// ---------------------------------------------------------------------------
// Existing helpers (unchanged behaviour)
// ---------------------------------------------------------------------------

export async function fetchUserDetails(): Promise<UserInfo | null> {
    const api = getApi();
    if (api) {
        const userInfo: UserInfo = await api.userInfo();
        return userInfo;
    }
    return null;
}

export async function fetchUserByEmail(email: string): Promise<User | null> {
    const api = getApi();
    if (api) {
        const result = await api.callApi({
            url: config.graphqlURL,
            method: 'POST',
            data: {
                query: `query {
                    users(where: {email: "${email}"}) {
                        ID
                        F2_ID
                        name
                        surname
                        email
                    }
                }`,
            },
        });
        return result.data?.users?.pop() || null;
    }
    return null;
}
